import { doc, getDoc, setDoc, updateDoc, serverTimestamp, getDocs, query, where, collection, orderBy, limit, deleteDoc } from 'firebase/firestore'
import { db } from '../config/firebase'
import AdminFunctions from './AdminFunctionsService'
import FirestoreMonitor from './FirestoreMonitor'

export type UserProfile = {
  uid: string
  email: string
  username: string
  companyName?: string
  role?: 'client' | 'admin'
  deleted?: boolean
  createdAt?: unknown
  updatedAt?: unknown
}

class ProfilesService {
  private col = 'profiles'
  private profileCache = new Map<string, { profile: UserProfile | null; timestamp: number }>()
  private CACHE_DURATION = 30000 // 30 sekund

  // Usunięto duplikatową metodę - używaj getProfile() zamiast getByUid()
  async getByUid(uid: string): Promise<UserProfile | null> {
    // Przekieruj na getProfile() żeby używać tego samego cache i logiki
    return this.getProfile(uid)
  }

  // Track ongoing requests to prevent duplicate calls
  private ongoingRequests = new Map<string, Promise<UserProfile | null>>()

  async getProfile(uid: string): Promise<UserProfile | null> {
    // Check cache first
    const cached = this.profileCache.get(uid)
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('📋 Using cached profile for:', uid)
      return cached.profile
    }
    
    // Check if request is already in progress
    const ongoingRequest = this.ongoingRequests.get(uid)
    if (ongoingRequest) {
      console.log('⏳ Profile request already in progress for:', uid)
      return ongoingRequest
    }
    
    // Create new request
    const requestPromise = this.fetchProfileFromFirestore(uid)
    this.ongoingRequests.set(uid, requestPromise)
    
    try {
      const profile = await requestPromise
      return profile
    } finally {
      // Clean up ongoing request
      this.ongoingRequests.delete(uid)
    }
  }
  
  private async fetchProfileFromFirestore(uid: string): Promise<UserProfile | null> {
    console.log('🔥 Fetching profile from Firestore for:', uid)
    const dref = doc(db, this.col, uid)
    const snap = await getDoc(dref)
    FirestoreMonitor.recordReads(1)
    const profile = snap.exists() ? { uid, ...(snap.data() as any) } : null
    
    // Cache result
    this.profileCache.set(uid, { profile, timestamp: Date.now() })
    console.log('✅ Profile cached for:', uid, profile ? 'found' : 'not found')
    
    return profile
  }

  async ensureProfile(uid: string, email: string, username: string, role: 'client' | 'admin', companyName?: string): Promise<UserProfile> {
    const dref = doc(db, this.col, uid)
    const snap = await getDoc(dref)
    FirestoreMonitor.recordReads(1)
    const defaultUsername = username || (email ? email.split('@')[0] : 'user')
    if (!snap.exists()) {
      const payload = { uid, email, username: defaultUsername, role, companyName, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }
      await setDoc(dref, payload)
      FirestoreMonitor.recordWrite()
      return payload as UserProfile
    } else {
      const data = snap.data() as any
      // Best-effort: keep email in sync and ensure role is set if missing
      const needsUpdate = (email && data.email !== email) || !data.role
      if (needsUpdate) {
        const updates = { ...data }
        if (email && data.email !== email) updates.email = email
        if (!data.role) updates.role = role
        updates.updatedAt = serverTimestamp()
        await setDoc(dref, updates)
        // Clear cache for updated profile
        this.profileCache.delete(uid)
        return { ...updates, uid }
      }
      // Cache existing profile
      const profile = { uid, ...(data as any) }
      this.profileCache.set(uid, { profile, timestamp: Date.now() })
      return profile
    }
  }

  async updateProfile(uid: string, updates: Partial<Pick<UserProfile, 'email' | 'username' | 'companyName' | 'role'>>): Promise<void> {
    const dref = doc(db, this.col, uid)
    const prev = await getDoc(dref)
    const data = prev.exists() ? prev.data() : {}
    await setDoc(dref, { ...data, ...updates, uid, updatedAt: serverTimestamp() })
    
    // Invalidate cache after update
    this.profileCache.delete(uid)
    console.log('🧹 Profile cache invalidated for:', uid)
  }

  async listProfiles(limitCount: number = 100): Promise<UserProfile[]> {
    const snap = await getDocs(query(collection(db, this.col), orderBy('createdAt', 'desc'), limit(limitCount)))
    FirestoreMonitor.recordReads(snap.size)
    const profiles = snap.docs.map(d => ({ uid: d.id, ...(d.data() as any) })) as UserProfile[]
    // Filter out deleted profiles
    return profiles.filter(profile => !profile.deleted)
  }

  async findByUsernameOrEmail(usernameOrEmail: string): Promise<UserProfile | null> {
    // Optymalizacja: pojedyncze zapytanie na podstawie formatu
    const isEmail = usernameOrEmail.includes('@')
    const field = isEmail ? 'email' : 'username'
    
    const snap = await getDocs(query(collection(db, this.col), where(field, '==', usernameOrEmail), limit(1)))
    FirestoreMonitor.recordReads(snap.size)
    if (!snap.empty) {
      const doc = snap.docs[0]
      return { uid: doc.id, ...doc.data() } as UserProfile
    }

    return null
  }

  async listAllProfiles(includeDeleted: boolean = false, limitCount: number = 100): Promise<UserProfile[]> {
    const snap = await getDocs(query(collection(db, this.col), orderBy('createdAt', 'desc'), limit(limitCount)))
    FirestoreMonitor.recordReads(snap.size)
    const profiles = snap.docs.map(d => ({ uid: d.id, ...(d.data() as any) })) as UserProfile[]
    
    if (includeDeleted) {
      return profiles // Return all profiles including deleted ones
    } else {
      return profiles.filter(profile => !profile.deleted) // Filter out deleted profiles
    }
  }

  async isEmailTaken(email: string, excludeUid?: string): Promise<boolean> {
    const snap = await getDocs(query(collection(db, this.col), where('email', '==', email), limit(1)))
    FirestoreMonitor.recordReads(snap.size)
    return snap.docs.some(d => d.id !== excludeUid)
  }

  async checkEmailStatus(email: string): Promise<{ exists: boolean; deleted: boolean; profile?: UserProfile }> {
    const snap = await getDocs(query(collection(db, this.col), where('email', '==', email), limit(1)))
    FirestoreMonitor.recordReads(snap.size)
    if (snap.empty) {
      return { exists: false, deleted: false }
    }
    
    const profile = { uid: snap.docs[0].id, ...snap.docs[0].data() } as UserProfile
    return { 
      exists: true, 
      deleted: profile.deleted || false,
      profile
    }
  }

  async isUsernameTaken(username: string, excludeUid?: string): Promise<boolean> {
    const snap = await getDocs(query(collection(db, this.col), where('username', '==', username), limit(1)))
    FirestoreMonitor.recordReads(snap.size)
    return snap.docs.some(d => d.id !== excludeUid)
  }

  async checkUsernameStatus(username: string): Promise<{ exists: boolean; deleted: boolean; profile?: UserProfile }> {
    const snap = await getDocs(query(collection(db, this.col), where('username', '==', username), limit(1)))
    FirestoreMonitor.recordReads(snap.size)
    if (snap.empty) {
      return { exists: false, deleted: false }
    }
    
    const profile = { uid: snap.docs[0].id, ...snap.docs[0].data() } as UserProfile
    return { 
      exists: true, 
      deleted: profile.deleted || false,
      profile
    }
  }

  async markAsDeleted(uid: string): Promise<void> {
    const dref = doc(db, this.col, uid)
    await updateDoc(dref, { 
      deleted: true,
      updatedAt: new Date()
    })
    FirestoreMonitor.recordWrite()
  }

  async restoreUser(uid: string): Promise<void> {
    const dref = doc(db, this.col, uid)
    await updateDoc(dref, { 
      deleted: false,
      updatedAt: new Date()
    })
    FirestoreMonitor.recordWrite()
  }

  async deleteProfile(uid: string): Promise<void> {
    const dref = doc(db, this.col, uid)
    await deleteDoc(dref)
    FirestoreMonitor.recordDelete()
  }

  async permanentlyDeleteUser(uid: string): Promise<{ authDeleted: boolean; profileDeleted: boolean; method: string }> {
    let authDeleted = false
    let profileDeleted = false
    let method = 'none'
    
    // NAJPIERW usuń profil z Firestore
    try {
      await this.deleteProfile(uid)
      profileDeleted = true
      console.log('✅ Successfully deleted user profile from Firestore')
    } catch (profileErr) {
      console.error('❌ Failed to delete user profile:', profileErr)
      throw new Error('Nie udało się usunąć profilu użytkownika')
    }
    
    // POTEM usuń z Firebase Auth using Cloud Functions
    try {
      await AdminFunctions.deleteAuthUser({ uid })
      authDeleted = true
      method = 'Cloud Functions'
      console.log('✅ Successfully deleted Firebase Auth user via Cloud Functions')
    } catch (authErr) {
      console.warn('⚠️ Firebase Auth deletion failed (but profile deleted):', authErr)
      method = 'failed'
    }
    
    return { authDeleted, profileDeleted, method }
  }

  async searchProfiles(searchTerm: string, includeDeleted: boolean = false, limitCount: number = 200): Promise<UserProfile[]> {
    const term = searchTerm.toLowerCase().trim()
    if (!term) return []

    try {
      // Przeszukaj po email
      const emailQuery = query(
        collection(db, this.col), 
        where('email', '>=', term),
        where('email', '<=', term + '\uf8ff'),
        limit(limitCount)
      )
      
      // Przeszukaj po username  
      const usernameQuery = query(
        collection(db, this.col), 
        where('username', '>=', term),
        where('username', '<=', term + '\uf8ff'), 
        limit(limitCount)
      )

      const [emailSnap, usernameSnap] = await Promise.all([
        getDocs(emailQuery).then(snap => { FirestoreMonitor.recordReads(snap.size); return snap }),
        getDocs(usernameQuery).then(snap => { FirestoreMonitor.recordReads(snap.size); return snap })
      ])

      const foundProfiles = new Map<string, UserProfile>()
      
      // Dodaj wyniki z email search
      emailSnap.docs.forEach(doc => {
        const profile = { uid: doc.id, ...doc.data() } as UserProfile
        if (profile.email.toLowerCase().includes(term)) {
          foundProfiles.set(doc.id, profile)
        }
      })
      
      // Dodaj wyniki z username search
      usernameSnap.docs.forEach(doc => {
        const profile = { uid: doc.id, ...doc.data() } as UserProfile
        if (profile.username.toLowerCase().includes(term)) {
          foundProfiles.set(doc.id, profile)
        }
      })

      let profiles = Array.from(foundProfiles.values())
      
      // Filtruj role admin i deleted (jeśli nie chcemy ich pokazywać)
      profiles = profiles.filter(profile => {
        if (profile.role === 'admin') return false
        if (!includeDeleted && profile.deleted) return false
        return true
      })

      return profiles.sort((a, b) => {
        // Sortuj po relevance - exact match na początku
        const aEmailExact = a.email.toLowerCase() === term
        const bEmailExact = b.email.toLowerCase() === term
        const aUsernameExact = a.username.toLowerCase() === term  
        const bUsernameExact = b.username.toLowerCase() === term
        
        if (aEmailExact || aUsernameExact) return -1
        if (bEmailExact || bUsernameExact) return 1
        
        return 0
      })
      
    } catch (error) {
      console.error('Błąd wyszukiwania profili:', error)
      return []
    }
  }
}

export default new ProfilesService()
